import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import { FaFilePdf, FaFileImage, FaUpload, FaEye, FaTimes, FaPaw, FaUserMd, FaFileAlt } from "react-icons/fa";
import '../App/modalstyle.css';
import { useNavigate } from "react-router-dom";

export default function AllPetLabTests() {
    const { t } = useContext(TranslatorContext);

    const [petLabTests, setPetLabTests] = useState([]);
    const [labTests, setLabTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Upload Modal States
    const [uploadModal, setUploadModal] = useState(false);
    const [selectedTest, setSelectedTest] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadErrors, setUploadErrors] = useState({});

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Filters
    const [filters, setFilters] = useState({
        customer_name: '',
        pet_name: '',
        status: '',
        lab_test_id: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchPetLabTests();
    }, [currentPage, filters]);

    const fetchPetLabTests = async () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams({
            page: currentPage,
            ...(filters.customer_name && { customer_name: filters.customer_name }),
            ...(filters.pet_name && { pet_name: filters.pet_name }),
            ...(filters.status && { status: filters.status }),
            ...(filters.lab_test_id && { lab_test_id: filters.lab_test_id })
        });

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/all-pet-lab-tests?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPetLabTests(response.data.petLabTests.data || []);
                setLabTests(response.data.labTests || []);
                setLastPage(response.data.petLabTests.last_page || 1);
                setTotal(response.data.petLabTests.total || 0);
                setPerPage(response.data.petLabTests.per_page || 10);
            }
        } catch (error) {
            console.error("Error fetching pet lab tests:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            customer_name: '',
            pet_name: '',
            status: '',
            lab_test_id: ''
        });
        setCurrentPage(1);
    };

    const openUploadModal = (test) => {
        setSelectedTest(test);
        setUploadFile(null);
        setUploadErrors({});
        setUploadModal(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                setUploadErrors({ file: ['Invalid file type. Only PDF, JPG, JPEG, PNG are allowed.'] });
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setUploadErrors({ file: ['File size must be less than 2MB.'] });
                return;
            }
            setUploadErrors({});
            setUploadFile(file);
        }
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            setUploadErrors({ file: ['Please select a file to upload.'] });
            return;
        }

        setUploadLoading(true);
        const token = localStorage.getItem("authToken");
        const formData = new FormData();
        formData.append('test_file', uploadFile);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-lab-tests-upload-file/${selectedTest.id}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data) {
                setUploadModal(false);
                setUploadFile(null);
                setSelectedTest(null);
                fetchPetLabTests();
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUploadErrors(error.response.data.errors || {});
            } else {
                console.error('Error uploading file:', error);
                setUploadErrors({ general: ['Failed to upload file. Please try again.'] });
            }
        } finally {
            setUploadLoading(false);
        }
    };

    const getFileIcon = (filePath) => {
        if (!filePath) return <FaUpload style={{ color: '#ffc107' }} />;
        const extension = filePath.split('.').pop().toLowerCase();
        if (extension === 'pdf') return <FaFilePdf style={{ color: '#dc3545' }} />;
        if (['jpg', 'jpeg', 'png'].includes(extension)) return <FaFileImage style={{ color: '#28a745' }} />;
        return <FaFileAlt style={{ color: '#6c757d' }} />;
    };

    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        return `${process.env.REACT_APP_API_URL}/storage/${filePath}`;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { class: 'mc-badge mc-badge-warning', text: 'Pending' },
            completed: { class: 'mc-badge mc-badge-success', text: 'Completed' },
            cancelled: { class: 'mc-badge mc-badge-danger', text: 'Cancelled' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <span className={config.class}>{config.text}</span>;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && currentPage === 1) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 mb-0">{t("loading")}...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('pet lab tests')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('appointments')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('lab tests')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-body">
                            <Row>
                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">{t('customer name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={filters.customer_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by customer')}
                                    />
                                </Col>

                                <Col xl={3} className="mb-3">
                                    <Form.Label className="fw-bold">{t('pet name')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="pet_name"
                                        value={filters.pet_name}
                                        onChange={handleFilterChange}
                                        placeholder={t('search by pet')}
                                    />
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">{t('status')}</Form.Label>
                                    <Form.Select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t('all')}</option>
                                        <option value="pending">{t('pending')}</option>
                                        <option value="completed">{t('completed')}</option>
                                        <option value="cancelled">{t('cancelled')}</option>
                                    </Form.Select>
                                </Col>

                                <Col xl={2} className="mb-3">
                                    <Form.Label className="fw-bold">{t('lab test')}</Form.Label>
                                    <Form.Select
                                        name="lab_test_id"
                                        value={filters.lab_test_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">{t('all tests')}</option>
                                        {labTests.map(test => (
                                            <option key={test.id} value={test.id}>{test.name}</option>
                                        ))}
                                    </Form.Select>
                                </Col>

                                <Col xl={2} className="mb-3 d-flex align-items-end">
                                    <ButtonComponent
                                        className="btn btn-secondary w-100"
                                        onClick={clearFilters}
                                    >
                                        <FaTimes className="me-1" /> {t('clear')}
                                    </ButtonComponent>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>

                {/* Table Section */}
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mc-card-title mb-0">{t('lab tests list')}</h5>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('appointment number')}</th>
                                                    <th>{t('pet name')}</th>
                                                    <th>{t('customer name')}</th>
                                                    <th>{t('lab test')}</th>
                                                    <th>{t('file')}</th>
                                                    <th>{t('date time')}</th>
                                                    <th>{t('doctor')}</th>
                                                    <th>{t('status')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody >
                                                {petLabTests.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="10" className="text-center">
                                                            {t('No lab tests found')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    petLabTests.map((test, index) => (
                                                        <tr key={test.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{(currentPage - 1) * perPage + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="mc-badge mc-badge-primary">
                                                                    {test.appointment?.appointment_number || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {test.appointment?.pet?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>{test.appointment?.customer?.name || 'N/A'}</td>
                                                            <td>
                                                                <span className="mc-badge mc-badge-info">
                                                                    {test.lab_test?.name || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                {test.test_file ? (
                                                                    <a
                                                                        href={getFileUrl(test.test_file)}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        style={{ textDecoration: 'none', fontSize: '20px' }}
                                                                        title="View File"
                                                                    >
                                                                        {getFileIcon(test.test_file)}
                                                                    </a>
                                                                ) : (
                                                                    <ButtonComponent
                                                                        type="button"
                                                                        className="btn btn-sm btn-warning"
                                                                        onClick={() => openUploadModal(test)}
                                                                        title="Upload File"
                                                                    >
                                                                        <FaUpload />
                                                                    </ButtonComponent>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    <div>{formatDateTime(test.created_at)}</div>

                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {test.appointment?.doctor?.name || 'N/A'}
                                                                </div>
                                                            </td>
                                                            <td>{getStatusBadge(test.status)}</td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Pet lab test edit") && (

                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => navigate(`/pet-lab-test-result/${test.id}`)}
                                                                            title="View Result"
                                                                        >
                                                                            visibility
                                                                        </ButtonComponent>
                                                                    )}
                                                                    <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons text-primary"
                                                                        onClick={() =>
                                                                            window.open(
                                                                                `${process.env.REACT_APP_URL}/lab-report/${test.id}`,
                                                                                '_blank'
                                                                            )
                                                                        }
                                                                        title="Print Invoice"
                                                                    >
                                                                        description
                                                                    </ButtonComponent>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Pagination */}
                                        {lastPage > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top px-3 pb-3">
                                                <div className="text-muted small">
                                                    {t('showing')} {(currentPage - 1) * perPage + 1} {t('to')} {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                    >
                                                        {t('previous')}
                                                    </ButtonComponent>
                                                    <span className="px-3 py-1 bg-light rounded">
                                                        {t('page')} {currentPage} {t('of')} {lastPage}
                                                    </span>
                                                    <ButtonComponent
                                                        className="btn btn-sm btn-outline-secondary"
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                        disabled={currentPage === lastPage}
                                                    >
                                                        {t('next')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Upload Modal */}
            <Modal
                show={uploadModal}
                onHide={() => setUploadModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {t('upload lab test result')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUploadSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {uploadErrors.general && (
                            <div className="alert alert-danger mb-3">
                                {uploadErrors.general}
                            </div>
                        )}

                        {selectedTest && (
                            <div className="alert alert-info mb-3">
                                <div><strong>{t('appointment')}:</strong> #{selectedTest.appointment?.appointment_number}</div>
                                <div><strong>{t('pet')}:</strong> {selectedTest.appointment?.pet?.name}</div>
                                <div><strong>{t('test')}:</strong> {selectedTest.lab_test?.name}</div>
                            </div>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">
                                {t('test file')} <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                type="file"
                                name="test_file"
                                className="custom-input"
                                onChange={handleFileChange}
                                isInvalid={!!uploadErrors.test_file}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <Form.Control.Feedback type="invalid">
                                {uploadErrors.test_file?.[0]}
                            </Form.Control.Feedback>
                            <small className="text-muted">
                                {t('allowed_formats')}: PDF, JPG, JPEG, PNG. {t('max_size')}: 2MB
                            </small>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUploadModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={uploadLoading}
                        >
                            {uploadLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            {t('upload')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style jsx>{`
                .custom-modal-dialog {
                    max-width: 500px;
                }
                .custom-modal-content {
                    border-radius: 12px;
                }
                .custom-modal-header {
                    background-color: #d7d8da;
                    border-bottom: none;
                }
                .custom-modal-header .btn-close {
                    filter: brightness(0) invert(1);
                }
                .custom-modal-title {
                    font-weight: 600;
                }
                .custom-modal-body {
                    padding: 20px;
                }
                .custom-modal-footer {
                    border-top: none;
                    padding: 15px 20px;
                }
                .custom-input:focus {
                    border-color: #3182ce;
                    box-shadow: 0 0 0 0.2rem rgba(8, 88, 247, 0.25);
                }
                .mc-table-action {
                    display: flex;
                    gap: 8px;
                }
                .mc-table-action .material-icons {
                    cursor: pointer;
                    font-size: 20px;
                }
                .mc-table-action .material-icons.edit {
                    color: #3182ce;
                }
                .mc-table-action .material-icons.delete {
                    color: #dc3545;
                }
            `}</style>
        </PageLayout>
    );
}